import React, { type JSX } from 'react';
import { Box, Typography, IconButton, Stack } from '@mui/material';
import { CustomTransComponent } from 'components/CustomTransComponent';
import { LoadGroupsListHierarchy } from './LoadGroupsListHierarchy';
import type { SelectedLoad } from './AddLoads';
import CloseIcon from '@mui/icons-material/Close';
import { InfoListItem } from '@brightlayer-ui/react-components';
import { useNewCustomTranslations } from 'hooks';
import { GroupPath } from 'components/GroupPath';

type Props = {
    selectedLoadIds: SelectedLoad[];
    onToggleLoad: (loadId: string, load?: SelectedLoad) => void;
    allowedModelCategory?: string;
    existingLoadIds?: string[];
};

export const AddMultiDeviceLoads = ({
    selectedLoadIds,
    onToggleLoad,
    allowedModelCategory,
    existingLoadIds = [],
}: Props): JSX.Element => {
    const loadNumberStyle = {
        fontSize: '14px',
        fontWeight: '600',
        backgroundColor: '#727E84',
        color: '#fff',
        width: '18px',
        height: '18px',
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };
    useNewCustomTranslations('WIDGET_MANAGEMENT');
    const getLoadId = (load: any): string => load?.id || load?.loadId || '';

    const getDisplayLoadNumber = (load: SelectedLoad): string => {
        if (load?.loadNumber === undefined || load?.loadNumber === null || load?.loadNumber === '') {
            return '';
        }

        const parsedLoadNumber = Number(load.loadNumber);

        return Number.isNaN(parsedLoadNumber) ? String(load.loadNumber) : String(parsedLoadNumber + 1);
    };

    return (
        <Box p={2} display="flex" flexDirection="column">
            <Box display="flex" justifyContent={'space-between'}>
                <Box p={2} sx={{ width: '49%', backgroundColor: '#f2f2f2' }}>
                    <Box className="max-height-570 bg-white">
                        <LoadGroupsListHierarchy
                            ignoreDefaultAccess={true}
                            selectedLoadIds={selectedLoadIds}
                            onToggleLoad={onToggleLoad}
                            allowedModelCategory={allowedModelCategory}
                            existingLoadIds={existingLoadIds}
                        />
                    </Box>
                </Box>

                <Box p={2} sx={{ width: '49%', backgroundColor: '#f2f2f2', borderRadius: '4px' }}>
                    <Typography fontSize={16} fontWeight={600} color="#424e54" variant="h6" mb={1}>
                        <CustomTransComponent translationKey={'WIDGET_MANAGEMENT:SELECT_LOADS'} />
                    </Typography>
                    <Typography fontSize={16} fontWeight={400} color="#424e54" variant="h6" mb={2}>
                        <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.SELECT_SIXTEEN_LOADS'} />
                    </Typography>
                    <Typography variant="h6" mb={1} fontSize={12} letterSpacing={2} fontWeight={600} color="#424e54">
                        <CustomTransComponent translationKey={'COMMON:SELECTED_LOADS'} /> ({selectedLoadIds.length}/16)
                    </Typography>

                    <Stack
                        spacing={1}
                        className="bg-white"
                        style={{ height: 'calc(100vh - 481px)', overflowY: 'scroll' }}
                    >
                        {selectedLoadIds.map((item) => (
                            <InfoListItem
                                key={getLoadId(item)}
                                rightComponent={
                                    <IconButton
                                        disabled={selectedLoadIds?.length < 2}
                                        onClick={() => onToggleLoad(getLoadId(item), item)}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                }
                                title=""
                                subtitle={[
                                    <Stack pl={'4px'} key={item?.id}>
                                        <Typography fontSize={14} fontWeight={400} color="#424e54">
                                            {item?.loadName ?? item?.name}
                                        </Typography>
                                        <GroupPath
                                            key={item?.id}
                                            pathName={
                                                Array.isArray(item?.groupPath) ? item.groupPath : [item?.groupPath]
                                            }
                                            deviceName={item?.deviceName}
                                            width="250px"
                                        />
                                    </Stack>,
                                ]}
                                leftComponent={[
                                    <Box key={item?.id} sx={loadNumberStyle}>
                                        {getDisplayLoadNumber(item)}
                                    </Box>,
                                ]}
                                sx={{
                                    '& .MuiList-root': {
                                        paddingBottom: 0,
                                    },
                                    '& .MuiListItem-root': {
                                        marginTop: 0,
                                    },
                                }}
                                dense
                                divider="full"
                                hidePadding
                            />
                        ))}
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
};
